import { useMemo } from 'react';
import { useGetCatalogoQuery, type Canal, type CatalogoResponse, type TipoCalculoPasso } from '../api';
import { useArgCatalogo } from './arg-catalogo';
import { urlImagemProduto } from './imagem';

/**
 * Imagem de cada produto do catálogo do canal, indexada pelo id do produto.
 *
 * Serve às listagens de itens já escolhidos (carrinho, conferência, conta), que
 * guardam só o id — a imagem não é copiada para dentro do item. O carrinho
 * conhece `idProduto` direto; a conta do totem de checkout vem do Sischef com
 * `codigoExterno`, que é o mesmo id em string (o payload grava
 * `String(idProduto)`), então lá o chamador converte antes de consultar.
 */
export function useImagensPorProduto(canal: Canal): Map<number, string | undefined> {
  const { data: catalogo } = useGetCatalogoQuery(useArgCatalogo(canal));

  return useMemo(() => {
    const mapa = new Map<number, string | undefined>();

    for (const grupo of catalogo?.grupos ?? []) {
      for (const produto of grupo.produtos) {
        if (mapa.has(produto.id)) continue;

        const imagem = produto.imagens.find((i) => i.preferencial) ?? produto.imagens[0];
        mapa.set(produto.id, urlImagemProduto(imagem, { largura: 144, altura: 144, modo: 'cover' }));
      }
    }

    return mapa;
  }, [catalogo]);
}

/** Tipo de cálculo de cada passo do catálogo: a listagem mostra "1/2 Calabresa" só em passo fracionado. */
export function tiposCalculoPorPasso(catalogo: CatalogoResponse | undefined): Map<number, TipoCalculoPasso> {
  const mapa = new Map<number, TipoCalculoPasso>()

  for (const grupo of catalogo?.grupos ?? []) {
    for (const produto of grupo.produtos) {
      for (const passo of produto.passos) mapa.set(passo.id, passo.tipoCalculo)
    }
  }

  return mapa
}

export function useTiposCalculoPorPasso(canal: Canal): Map<number, TipoCalculoPasso> {
  const { data: catalogo } = useGetCatalogoQuery(useArgCatalogo(canal))

  return useMemo(() => tiposCalculoPorPasso(catalogo), [catalogo])
}
